import React, { useMemo } from 'react';
import { Button, Flex, Layout, Input, Grid, Skeleton } from 'antd';
import { styled } from 'styled-components';
import Icon, {
  GiftOutlined,
  BellOutlined,
  MessageOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks/use-app-selector';
import { UserProfile } from './UserProfile';
import { AppointmentNotifications } from './AppointmentNotifications';
import { openCelloWidget } from '../../utils';
import {
  BEAMER_ID,
  CELLO_PRODUCT_ID,
  NOTIFICATION_ELEMENT_ID,
} from '../../constants';
import EVENTS from '../../constants/event-bus-events';
import { t } from '../../utils/i18n';
import { navigateTo } from '../../actions';
import { useGlobals } from '../../globals-context'; // TODO: Replace with shared context
import { getAppSettings } from '../../selectors';
import { getHelpCenterLink } from '../../shared/utils';
import { UserActionNotifications } from './user-action-notifications';

const { Header: AntdHeader } = Layout;
const { useBreakpoint } = Grid;

const StyledHeader = styled(AntdHeader)<{ $collapsed?: boolean }>`
  background: ${({ theme }) => theme.antd.colorWhite};
  padding: 0 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.antd.colorBorderSecondary};
  width: 100%;
  margin-left: auto;
  flex: 0 0 auto;
  margin-bottom: 1px;
  line-height: normal;
  transition: width 200ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const StyledHeaderIcon = styled(Icon)`
  font-size: 2rem;
  cursor: pointer;
  outline: none;
  color: ${({ theme }) => theme.antd.colorTextSecondary};

  &:hover {
    color: ${({ theme }) => theme.antd.colorPrimary};
  }
`;

const StyledLink = styled.a`
  outline: none;
  line-height: normal;
  color: ${({ theme }) => theme.antd.colorTextSecondary};

  &:hover {
    color: ${({ theme }) => theme.antd.colorPrimary};
  }
`;

const StyledSearchWrapper = styled.div`
  position: relative;
  width: 28rem;

  @media (max-width: 768px) {
    width: 26rem;
  }

  @media (max-width: 481px) {
    width: 22rem;
  }
`;
const StyledDynamicSearchInput = styled.div`
  height: 28px;
  position: absolute;
  top: 3px;
  left: 0;
  z-index: 1;
`;

export function Header({
  sideMenuCollapsed = false,
  onSideMenuCollapseChange,
}: {
  sideMenuCollapsed?: boolean;
  onSideMenuCollapseChange?: (collapsed: boolean) => void;
}) {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { trackMethod, eventBus } = useGlobals() as any;

  const shouldShowProductNotification = Boolean(BEAMER_ID);

  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const currentAccount = useAppSelector((state) => state.currentAccount);
  const sessionPermissions = useAppSelector(
    (state) => state.sessionPermissions
  );

  const isDataLoading = useMemo(
    () => Object.values(sessionPermissions).some((value) => value === null),
    [sessionPermissions]
  );

  const {
    company,
    whiteLabelConfiguration: { schemaless: whiteLabelSchemaless },
  } = useAppSelector((state) => getAppSettings(state)) || {};

  const locale = currentAccount?.locale || 'en';
  const homeHelpCenterLink = getHelpCenterLink({
    key: 'home',
    config: whiteLabelSchemaless,
    locale,
    companyName: company,
  });

  const handleOnCollapseButtonClick = () => {
    if (onSideMenuCollapseChange) {
      onSideMenuCollapseChange(!sideMenuCollapsed);
    }
    if (!sideMenuCollapsed) {
      eventBus.emit(EVENTS.SIDE_MENU_COLLAPSE);
    } else {
      eventBus.emit(EVENTS.SIDE_MENU_EXPAND);
    }
  };

  const handleOnSearchInputWrapperClick = () => {
    /*
      Note: This dispatch loads the Search micro-frontend,
      mounts the SearchBar component App into the .as-SearchWrapper-searchInput element,
      and then focuses the input field rendered by the SearchBar component.
    */
    dispatch(navigateTo('#/search'));
  };

  const handleOnReferralButtonClick = () => {
    trackMethod('top_menu_item_clicked', {
      platform: 'booking',
      location: 'top_menu_button',
      button_type: 'referral_earn',
    });
    openCelloWidget();
  };

  const handleOnMessengerIconClick = () => {
    trackMethod('top_menu_item_clicked', {
      platform: 'booking',
      location: 'top_menu_button',
      button_type: 'messenger',
    });
  };

  const handleOnProductNotificationIconClick = () => {
    trackMethod('top_menu_item_clicked', {
      platform: 'booking',
      location: 'top_menu_button',
      button_type: 'product_notifications',
    });
  };

  return (
    <>
      {screens.md && <UserActionNotifications />}
      <StyledHeader>
        <Flex justify="space-between" align="center" style={{ height: '100%' }}>
          <Flex align="center" gap="16px">
            {screens.md && (
              <Button
                type="text"
                icon={
                  sideMenuCollapsed ? (
                    <MenuUnfoldOutlined />
                  ) : (
                    <MenuFoldOutlined />
                  )
                }
                onClick={handleOnCollapseButtonClick}
                style={{ fontSize: '16px' }}
              />
            )}
            <StyledSearchWrapper onClick={handleOnSearchInputWrapperClick}>
              <Input.Search
                allowClear
                placeholder="Find customers or appointments"
                style={{ width: screens.md ? 280 : '100%' }}
              />
              {
                // TODO: Improve Search bar later
                <StyledDynamicSearchInput
                  className="as-SearchWrapper-searchInput"
                  style={{ width: `calc(${screens.md ? 280 : '100%'} - 30px)` }}
                />
              }
            </StyledSearchWrapper>
          </Flex>
          <Flex align="center" gap="16px">
            {screens.md && CELLO_PRODUCT_ID && (
              <Flex
                gap="16px"
                style={{ position: 'relative' }}
                className="cello-launcher-announcement"
              >
                <Button
                  size="middle"
                  color="primary"
                  variant="outlined"
                  icon={<GiftOutlined />}
                  onClick={handleOnReferralButtonClick}
                >
                  {t('button.referral.title')}
                  <div className="cello-launcher" />
                </Button>
              </Flex>
            )}
            {screens.md && (
              <Flex gap="16px">
                <AppointmentNotifications />
                {currentMerchant.featureToggles.messaging && (
                  <StyledLink
                    data-route
                    href="#/messenger/conversations"
                    onClick={handleOnMessengerIconClick}
                  >
                    <StyledHeaderIcon
                      component={
                        MessageOutlined as React.ForwardRefExoticComponent<any>
                      }
                    />
                  </StyledLink>
                )}
                {shouldShowProductNotification && (
                  <StyledHeaderIcon
                    id={NOTIFICATION_ELEMENT_ID}
                    component={
                      BellOutlined as React.ForwardRefExoticComponent<any>
                    }
                    onClick={handleOnProductNotificationIconClick}
                  />
                )}
              </Flex>
            )}
            {isDataLoading ? (
              <Skeleton.Input
                active
                size="small"
                style={{ height: 44, width: 20 }}
              />
            ) : (
              <UserProfile homeHelpCenterLink={homeHelpCenterLink} />
            )}
          </Flex>
        </Flex>
      </StyledHeader>
    </>
  );
}
